import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { Auth, Hub } from 'aws-amplify';
import { useAppDispatch, useAppSelector } from 'app/store';
import type { RootState } from 'app/store/rootReducer';
import { selectAuth, signOut } from 'app/store/authSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { SnackbarUtils } from 'app/shared-components/util/SnackbarUtils';
import { CDK_REFUSAL_EVENT } from 'app/services/session/cdkRefusalSignal';
import {
  SIGN_OUT_TOAST_MS,
  SessionCheckOptions,
  isPaidolSessionCheckInFlight,
  readLocalPaidolSession,
  runPaidolSessionCheck,
} from 'app/services/session/paidolSessionCheck';

/** At most one refresh-triggered check per minute (Req 2.6). */
export const MIN_REFRESH_CHECK_INTERVAL_MS = 60_000;

/**
 * Refresh failures that mean the session is over: revoked, expired or disabled
 * (NotAuthorizedException), or a deleted user under the app client's LEGACY
 * user-existence errors (UserNotFoundException). Anything else (offline,
 * throttled) is not a reason to sign out.
 */
const AUTH_FAILURE_CODES = new Set(['NotAuthorizedException', 'UserNotFoundException']);

/**
 * Pre-token's rejections of a user with no company left
 * (speedchainr2PreTokenGeneration/src/index.js:250, :300, :334), which sign out
 * (decided Q7). Cognito reports any error the Lambda throws as
 * UserLambdaValidationException, a timeout or a throttle included, so the
 * message decides.
 */
const NO_MEMBERSHIP_MESSAGES = [
  'does not have any active Paidol memberships',
  'does not have any Paidol accounts',
  'has no linked Paidol membership',
];

export const isAuthRefreshFailure = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { code, name, message } = error as { code?: unknown; name?: unknown; message?: unknown };
  const type = typeof code === 'string' ? code : typeof name === 'string' ? name : '';
  if (AUTH_FAILURE_CODES.has(type)) return true;
  return (
    type === 'UserLambdaValidationException' &&
    typeof message === 'string' &&
    NO_MEMBERSHIP_MESSAGES.some((noMembership) => message.includes(noMembership))
  );
};

const hasLocalSession = (): boolean => {
  const { paidolId, sessionId } = readLocalPaidolSession();
  return Boolean(paidolId && sessionId);
};

// Kill switch for the refresh trigger alone (Req 2.6b). Baked in at build time.
const isRefreshCheckEnabled = (): boolean => process.env.REACT_APP_SESSION_CHECK_ON_REFRESH !== 'false';

/**
 * SEE-1738: runs the paidol session check for CDK traffic, which the Amplify
 * middleware's own check never sees. Three triggers:
 *
 * - a CDK transport reported a refusal (cdkRefusalSignal): act on every outcome;
 * - Amplify refreshed the tokens (Hub `tokenRefresh`): act on removal and a
 *   company mismatch, never on a rotated session, so single-session is no
 *   stricter than today (SEE-1760);
 * - the refresh was refused for an auth reason (Hub `tokenRefresh_failure`):
 *   sign out.
 *
 * Every `CognitoUser.refreshSession` emits `tokenRefresh`, the app's own
 * included (company switch, the SEE-1583 re-mint, passkey sign-in). So both
 * check triggers stand down while a switch is running, while a check is in
 * flight, and before a company is selected; otherwise a refresh the app made
 * could start a check that reloads mid-switch (the SPE-1474 loop).
 *
 * Mounted once in App.js, inside SnackbarProvider so its toasts render. The
 * listeners are registered once, so they read the store at event time.
 */
function CdkSessionGuard(): null {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { user } = useAppSelector(selectAuth);

  const inFlightRef = useRef(false);
  const lastIatRef = useRef<number | null>(null);
  const lastRefreshCheckAtRef = useRef<number | null>(null);
  const signingOutRef = useRef(false);

  // Re-arm the refresh-failure sign-out once the user is gone (or back).
  useEffect(() => {
    if (!user) signingOutRef.current = false;
  }, [user]);

  useEffect(() => {
    const isSuppressed = (): boolean => {
      const { isSwitching, selectedPaidolId } = selectUserCompanies(store.getState());
      return inFlightRef.current || isPaidolSessionCheckInFlight() || isSwitching || !selectedPaidolId;
    };

    const run = async (options: SessionCheckOptions): Promise<void> => {
      if (isSuppressed()) return;
      inFlightRef.current = true;
      try {
        await runPaidolSessionCheck(dispatch, options);
      } finally {
        inFlightRef.current = false;
      }
    };

    const onRefusal = (): void => {
      void run({ ignoreRotated: false });
    };

    const onTokenRefresh = async (): Promise<void> => {
      if (!isRefreshCheckEnabled() || isSuppressed()) return;
      let iat: unknown;
      try {
        const session = await Auth.currentSession();
        iat = session.getIdToken().payload.iat;
      } catch {
        return;
      }
      // State may have moved on while the session was read.
      if (isSuppressed() || typeof iat !== 'number' || iat === lastIatRef.current) return;
      const now = Date.now();
      if (
        lastRefreshCheckAtRef.current !== null &&
        now - lastRefreshCheckAtRef.current < MIN_REFRESH_CHECK_INTERVAL_MS
      ) {
        return;
      }
      lastIatRef.current = iat;
      lastRefreshCheckAtRef.current = now;
      await run({ ignoreRotated: true });
    };

    const onRefreshFailure = (error: unknown): void => {
      // No user: the 10-minute getCurrentUser tick (or a sign-out) got there first.
      if (!isAuthRefreshFailure(error) || !hasLocalSession() || !selectAuth(store.getState()).user) return;
      if (signingOutRef.current) return;
      signingOutRef.current = true;
      SnackbarUtils.enqueue('Your session has ended. Please sign in again.', {
        variant: 'warning',
        autoHideDuration: SIGN_OUT_TOAST_MS,
      });
      dispatch(signOut());
    };

    window.addEventListener(CDK_REFUSAL_EVENT, onRefusal);
    const stopListening = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'tokenRefresh') {
        void onTokenRefresh();
      } else if (payload.event === 'tokenRefresh_failure') {
        onRefreshFailure(payload.data);
      }
    });

    return () => {
      window.removeEventListener(CDK_REFUSAL_EVENT, onRefusal);
      stopListening();
    };
  }, [dispatch, store]);

  return null;
}

export default CdkSessionGuard;
